import { matchPath } from 'react-router-dom';
import { buildingInspectionsPath } from '../constants/jobModel';

/**
 * Building Line-2 menu.
 * Overview · SS Units · inspection modules that apply (Chiller, Boiler, …) ·
 * Floor plan · Inspections · Paperwork · Documents · Checklists
 */
export const BUILDING_PEER_IDS = [
  'overview',
  'units',
  'floor-plan',
  'inspections',
  'paperwork',
  'documents',
  'checklists',
];

/** Chiller keeps its existing page. Later modules use /inspection/:CODE. */
export function inspectionModulePath(buildingId, moduleCode) {
  const code = String(moduleCode || '').toUpperCase();
  if (code === 'CHILLER') return `/buildings/${buildingId}/chiller`;
  return `/buildings/${buildingId}/inspection/${encodeURIComponent(code)}`;
}

function inspectionPeers(buildingId, opts = {}) {
  const listed = Array.isArray(opts.inspectionModules)
    ? opts.inspectionModules
    : opts.hasChiller
      ? [{ module_code: 'CHILLER', label: 'Chiller' }]
      : [];
  const seen = new Set();
  const peers = [];
  for (const mod of listed) {
    const code = String(mod?.module_code || mod?.code || '').toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    peers.push({
      id: `insp-${code}`,
      label: mod.label || code,
      to: inspectionModulePath(buildingId, code),
    });
  }
  return peers;
}

/** Line-2 sections under a building. Overview = default hub at `/buildings/:id`. */
export function getBuildingPeerNav(buildingId, pathname, opts = {}) {
  const id = String(buildingId);
  const peers = [
    { id: 'overview', label: 'Overview', to: `/buildings/${id}` },
    { id: 'units', label: 'SS Units', to: `/buildings/${id}/units` },
    ...inspectionPeers(id, opts),
    { id: 'floor-plan', label: 'Floor plan', to: `/buildings/${id}/floor-plan` },
    { id: 'inspections', label: 'Inspections', to: buildingInspectionsPath(id) },
    { id: 'paperwork', label: 'Paperwork', to: `/buildings/${id}/paperwork` },
    { id: 'documents', label: 'Documents', to: `/buildings/${id}/documents` },
    { id: 'checklists', label: 'Checklists', to: `/buildings/${id}/checklists` },
  ];
  const activeId = detectActivePeer(id, pathname);
  return peers.map((p) => ({ label: p.label, to: p.to, active: p.id === activeId }));
}

function detectActivePeer(buildingId, pathname) {
  const p = pathname || '';
  if (p === `/buildings/${buildingId}`) return 'overview';
  if (p === `/jobs/building/${buildingId}`) return 'inspections';
  if (/^\/jobs\/\d+(?:\/|$)/.test(p)) return 'inspections';
  if (
    p.startsWith(`/buildings/${buildingId}/units`) ||
    p.startsWith(`/buildings/${buildingId}/equipment-units`)
  ) {
    return 'units';
  }
  if (p.startsWith(`/buildings/${buildingId}/floor-plan`)) return 'floor-plan';
  if (p.startsWith(`/buildings/${buildingId}/paperwork`)) return 'paperwork';
  if (p.startsWith(`/buildings/${buildingId}/chiller`)) return 'insp-CHILLER';
  const inspMatch = p.match(new RegExp(`^/buildings/${buildingId}/inspection/([^/]+)`));
  if (inspMatch) return `insp-${decodeURIComponent(inspMatch[1]).toUpperCase()}`;
  if (p.startsWith(`/buildings/${buildingId}/documents`)) return 'documents';
  if (p.startsWith(`/buildings/${buildingId}/checklists`)) return 'checklists';
  return null;
}

export function unitsListPath(buildingId, ssCode) {
  const base = `/buildings/${buildingId}/units`;
  if (ssCode) return `${base}?ss=${encodeURIComponent(ssCode)}`;
  return base;
}

/**
 * Breadcrumb ancestors ?? current page title is yy (large) in the nav row.
 * Building hub `/buildings/:id` is the main page: yy = building name, trail = [Buildings].
 */
export function buildBuildingTrail({
  pathname,
  search = '',
  buildingId,
  buildingName,
  extraTrail = [],
  unitsSsCode,
}) {
  const trail = [{ label: 'Buildings', to: '/buildings' }];
  if (!buildingId) return trail;

  const id = String(buildingId);
  const name = (buildingName && String(buildingName).trim()) || `Building #${id}`;
  const path = pathname || '';
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search || ''
  );

  if (path === `/buildings/${id}`) {
    return trail;
  }

  trail.push({ label: name, to: `/buildings/${id}` });

  const unitDetail = matchPath({ path: '/buildings/:bid/units/:unitId', end: true }, path);
  if (unitDetail?.params.bid === id) {
    const ss = unitsSsCode || params.get('ss');
    trail.push({ label: 'SS Units', to: unitsListPath(id, ss || undefined) });
    return trail;
  }

  const equipDetail = matchPath({ path: '/buildings/:bid/equipment-units/:unitId', end: true }, path);
  if (equipDetail?.params.bid === id) {
    trail.push({ label: 'SS Units', to: `/buildings/${id}/units` });
    extraTrail.forEach((seg) => {
      if (seg?.label && seg?.to) trail.push({ label: seg.label, to: seg.to });
    });
    return trail;
  }

  if (path === `/buildings/${id}/units`) {
    if (params.get('ss')) {
      trail.push({ label: 'SS Units', to: `/buildings/${id}/units` });
    }
    return trail;
  }

  if (/^\/jobs\/\d+(?:\/|$)/.test(path)) {
    trail.push({ label: 'Inspections', to: buildingInspectionsPath(id) });
  }

  return trail;
}

/** Portfolio list / new building ?? no building id. */
export function buildPortfolioTrail(pathname) {
  if (pathname === '/buildings/new') {
    return [{ label: 'Buildings', to: '/buildings' }];
  }
  if (pathname === '/buildings' || pathname.startsWith('/buildings?')) {
    return [];
  }
  return [];
}

/**
 * Buildings portfolio Line-2 peers (same SubPageNav style as building / Application).
 * All · Full agent · Partly SS only · New building · Inactived list
 */
export function getBuildingsPortfolioNav(pathname, search = '') {
  const peers = [
    { id: 'all', label: 'All', to: '/buildings' },
    { id: 'full', label: 'Full agent', to: '/buildings?scope=full' },
    { id: 'ss_only', label: 'Partly SS only', to: '/buildings?scope=ss_only' },
    { id: 'add', label: 'New building', to: '/buildings/new' },
    { id: 'deferred', label: 'Starts later', to: '/buildings?contract=deferred' },
    { id: 'ended', label: 'Ended', to: '/buildings?contract=ended' },
    { id: 'inactive', label: 'Inactived list', to: '/buildings?inactive=1' },
  ];
  const activeId = detectPortfolioPeer(pathname, search);
  return peers.map((p) => ({ label: p.label, to: p.to, active: p.id === activeId }));
}

function detectPortfolioPeer(pathname, search = '') {
  const p = pathname || '';
  if (p === '/buildings/new') return 'add';
  if (p !== '/buildings') return null;

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search || '');
  if (params.get('inactive') === '1' || params.get('trash') === '1') return 'inactive';
  const contract = String(params.get('contract') || '').toLowerCase();
  if (contract === 'deferred' || contract === 'later') return 'deferred';
  if (contract === 'ended') return 'ended';
  const scope = params.get('scope');
  if (scope === 'full') return 'full';
  if (scope === 'ss_only') return 'ss_only';
  return 'all';
}

export function resolveBuildingNav({
  pathname,
  search = '',
  buildingId,
  buildingName,
  extraTrail = [],
  unitsSsCode,
  hasChiller = false,
  inspectionModules,
}) {
  if (!buildingId) {
    const showPortfolioPeers = pathname === '/buildings' || pathname === '/buildings/new';
    return {
      trail: buildPortfolioTrail(pathname),
      subPages: showPortfolioPeers ? getBuildingsPortfolioNav(pathname, search) : [],
    };
  }
  return {
    trail: buildBuildingTrail({
      pathname,
      search,
      buildingId,
      buildingName,
      extraTrail,
      unitsSsCode,
    }),
    subPages: getBuildingPeerNav(buildingId, pathname, { hasChiller, inspectionModules }),
  };
}
